// packages/core/src/learning/schools/fire-movement.ts
// ─────────────────────────────────────────────────────────────────────────
// Authored exhibit for "FIRE movement". Matches boglehead-indexing.ts gold
// standard.
//
// ID-bound fields (the validator + compiler guard these):
//   - figures:        FigureId[]  — held [] for the post-authoring cross-ref
//                     pass. Worldview names: vicki-robin, mr-money-mustache,
//                     jl-collins, grant-sabatier.
//   - applyActions:   ApplyAction union — type-checked at compile time. Only
//                     live/allowed action types emitted.
//   - featuredLessonIds / debateIds — [] until the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const FIRE_MOVEMENT_EXHIBIT: SchoolExhibitEntry = {
	id: 'fire-movement',
	tagline: 'FIRE believes money should buy back your time.',
	worldviewSummary:
		'FIRE is paying attention to the link between savings rate, invested assets, spending needs, and freedom. This lens protects you from drifting through decades of work without ever asking what your money is buying you. It accepts a real tradeoff: more saving now can create more options later. The blind spot is that FIRE can become too spreadsheet driven, too extreme, or too dismissive of people with lower income, family obligations, health limits, or expensive cities. At its best, FIRE is not about hating work. It is about building enough control that work becomes a choice instead of a trap.',
	coreBeliefs: [
		'FIRE believes your savings rate is not just a budget number. It is a freedom lever.',
		'FIRE believes invested assets can eventually replace part or all of your paycheck.',
		'FIRE believes lifestyle inflation quietly steals years of future choice.',
		'FIRE believes work feels different when you are not financially trapped inside it.',
		'FIRE believes the goal is not always quitting forever. The goal is having options.',
		'FIRE believes small choices matter because time turns repeated behavior into a life pattern.',
	],
	warnsAgainst: [
		'Spending every raise before it changes your future.',
		'Confusing a high income with actual freedom.',
		'Letting normal life defaults decide how long you stay dependent on a paycheck.',
		'Using FIRE math as a way to shame yourself or ignore real life constraints.',
	],
	helps: 'This lens helps people who want more control over their time, work, location, and life pace. It may not fit when the plan becomes extreme, lonely, joyless, or blind to income limits and family responsibilities.',
	figures: [],
	canon: [
		{
			title: 'Your Money or Your Life',
			author: 'Vicki Robin',
			oneLineIdea:
				'Track money against life energy, not just dollars spent.',
		},
		{
			title: 'The Shockingly Simple Math Behind Early Retirement',
			author: 'Mr. Money Mustache',
			oneLineIdea:
				'Savings rate can change your freedom timeline more than most people realize.',
		},
		{
			title: 'The Simple Path to Wealth',
			author: 'JL Collins',
			oneLineIdea:
				'Simple long term investing can support freedom when behavior stays steady.',
		},
		{
			title: 'Financial Freedom',
			author: 'Grant Sabatier',
			oneLineIdea:
				'Income, saving, and investing can be aimed at buying back control of your time.',
		},
		{
			title: 'The 4 percent rule',
			oneLineIdea:
				'A withdrawal rule of thumb gives FIRE math a starting point, not a personal guarantee.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-fire-movement',
	debateIds: [],
	applyActions: [
		{
			label: 'See what your savings rate does to your freedom date',
			action: {
				type: 'analyzeSavingsRate',
				label: 'Check your savings rate',
			},
		},
		{
			label: 'Build a freedom plan around time, not status',
			action: {
				type: 'buildFreedomPlan',
				label: 'Build your freedom plan',
			},
		},
		{
			label: 'Test how a higher savings rate changes your timeline',
			action: {
				type: 'openSimulator',
				label: 'Simulate a higher savings rate',
				scenarioType: 'aggressive-savings',
			},
		},
		{
			label: 'Check how much runway changes your work options',
			action: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
		},
	],
	marketingHooks: [
		{
			line: 'FIRE believes your paycheck should eventually buy your time back.',
			kind: 'hook',
		},
		{
			line: 'The hidden cost of lifestyle creep is not money. It is years of your life.',
			kind: 'hook',
		},
		{
			line: 'What would change if work became optional before old age?',
			kind: 'interview-question',
		},
		{
			line: 'Ask someone pursuing FIRE what they gave up, then ask what it gave back.',
			kind: 'creator-prompt',
		},
		{
			line: 'Nisk lets people learn the FIRE lens, then test the timeline on their own numbers.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience how savings rate changes freedom faster than most raises do.',
			kind: 'creator-prompt',
		},
		{
			line: 'What does FIRE get right about freedom, and where does it become too extreme?',
			kind: 'interview-question',
		},
		{
			line: 'FIRE is not about hating work. It is about refusing to be trapped by it.',
			kind: 'hook',
		},
	],
	revision: 1,
};
